// Контрактът на споделената таблица за модул „Справки".
//
// Тук живеят САМО типове. Една справка описва колоните си декларативно —
// какъв вид клетка, какъв филтър в хедъра, участва ли в реда „Общо", как
// излиза в Excel — а не пише маркъп. Рендерът е в DataCell.tsx, филтърните
// контроли са в filters/, самата композиция е в DataTable.tsx.
//
// Стъпва на @tanstack/react-table v8 (headless — нула чужд CSS), същата
// версия като shared/ui/DataTable в портала, за да е един и същ моделът на
// работа в двата продукта.

import type { ReactNode } from "react";
import type { RowData } from "@tanstack/react-table";

/* ────────────────────────────── видове клетки ───────────────────────────── */

/** Как DataCell рисува стойността. Стойността идва от `accessorFn`. */
export type CellKind =
  | "text" // текст; при `lines: 2` втори, помътнен ред
  | "num" // число, mono, дясно подравнено
  | "money" // сума през fmtEuro; отрицателното в danger, нулата помътнена
  | "dt" // дата + час; датата само когато периодът е повече от един ден
  | "min" // продължителност в минути → „42 мин" / „1 ч 16 мин"
  | "dot" // точка за статус + етикет
  | "chip" // бадж по тон
  | "pct" // процент
  | "bar"; // хоризонтална лента спрямо максимума в колоната

/** Тонове — всеки мапва към семантичен токен, никакъв литерален цвят. */
export type CellTone = "neutral" | "brand" | "ok" | "warn" | "danger" | "info";

/** Стойността, която `accessorFn` връща за не-скаларните видове. */
export interface DotCellValue {
  tone: CellTone;
  label: string;
  /**
   * Кръгче с контур вместо запълнено — тих втори признак В САМАТА точка, без
   * втора колона и без нито една дума. Ползва се там, където редът има подвид,
   * който не бива да се обявява с надпис.
   */
  outline?: boolean;
}
export interface ChipCellValue {
  tone: CellTone;
  label: string;
}
export interface TextCellValue {
  primary: string;
  secondary?: string | null;
  /** Малък етикет до основния текст (напр. „Обедно" в „Асортименти"/„Категории"). */
  tag?: ChipCellValue | null;
}
export interface BarCellValue {
  value: number;
  max: number;
  label?: string;
}

/** Настройките на клетката, дискриминирани по `kind`. */
export type CellOptions =
  | {
      kind: "text";
      lines?: 1 | 2;
      /** Целият текст при посочване с мишката — за колона, в която дългият текст се реже (забележка). */
      fullTextOnHover?: boolean;
    }
  | { kind: "num"; decimals?: number }
  | {
      kind: "money";
      /** Нулата се рисува помътнена („тих нул"), както в дизайна. */
      mutedZero?: boolean;
      /** Положителна стойност се рисува като „−X" в danger (отстъпка, сторно). */
      negative?: boolean;
      /** По-плътен шрифт — за колоната „Сума". */
      strong?: boolean;
    }
  | { kind: "dt"; withDate?: "auto" | "always" | "never" }
  | { kind: "min"; warnAbove?: number }
  | { kind: "dot" }
  | { kind: "chip" }
  | { kind: "pct"; decimals?: number; trim?: boolean }
  | { kind: "bar" };

/* ──────────────────────────── филтър в колоната ─────────────────────────── */

export interface ChecklistOption {
  value: string;
  label: string;
}

/**
 * Филтърът, който се отваря от фунията в хедъра на колоната.
 *
 * `checklist` без `options` вади стойностите от `getFacetedUniqueValues()` —
 * работи САМО в клиентски режим. В сървърен режим наборът е само текущата
 * страница и списъкът би лъгал, затова там `options` е ЗАДЪЛЖИТЕЛЕН и идва от
 * справочна заявка (сервитьори, методи на плащане…).
 */
export type ColumnFilterSpec =
  | { kind: "text"; placeholder?: string }
  | { kind: "numberRange" }
  | { kind: "dateRange" }
  | { kind: "checklist"; options?: ChecklistOption[] };

/* ─────────────────────── meta на колоната (augmentation) ────────────────── */

export interface DataTableColumnMeta {
  /**
   * Име на колоната за панела „Колони" и за менюто в хедъра. Нужно е, когато
   * `header` е празен по дизайн (напр. точката за статус) — иначе човек вижда
   * ред без надпис и не знае какво изключва.
   */
  label?: string;
  /** Как се рисува клетката. Липсва ли — рендерът е суровата стойност. */
  cell?: CellOptions;
  /** Фунията в хедъра. Липсва ли — колоната не се филтрира. */
  filter?: ColumnFilterSpec;
  /** Скрий под този праг. Прагът идва от useViewport, не от собствен matchMedia. */
  hideBelow?: "narrow" | "tablet";
  /**
   * Колоната съществува, но е ИЗГАСЕНА по подразбиране — стои в панела
   * „Колони" и се пали с една отметка. За числа, които трябват рядко, но
   * когато трябват, няма откъде другаде да се вземат.
   * Като `hideBelow`: изричният избор на потребителя бие подразбирането.
   */
  defaultHidden?: boolean;
  /** Участие в реда „Общо". `sum` е за пари и количества, `count` за брой редове. */
  total?: "sum" | "count";
  /**
   * Как се рисува клетката в реда „Общо", когато колоната има СОБСТВЕН
   * рендер (`columnDef.cell`) вместо декларативен `meta.cell`. Без това
   * тоталът излиза като голо число, без валута.
   */
  totalCell?: CellOptions;
  /** Ролята в мобилната карта. `hidden` = не се показва на телефон. */
  mobileRole?: "title" | "strong" | "meta" | "label" | "hidden";
  /** Excel: заглавие и вид на стойността. Числата излизат като ЧИСЛА, не низове. */
  excel?: {
    header?: string;
    format?: "number" | "money" | "date" | "text";
  };
  /** Забрани скриването — колоната е носеща за смисъла на реда. */
  alwaysVisible?: boolean;
  /** Подравняване; по подразбиране се извежда от `cell.kind`. */
  align?: "left" | "right" | "center";
}

declare module "@tanstack/react-table" {
  // Разширява `column.columnDef.meta` с полетата по-горе. Параметрите на типа
  // са задължителни от подписа на TanStack и не се ползват тук.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-object-type
  interface ColumnMeta<TData extends RowData, TValue>
    extends DataTableColumnMeta {}
}

/* ──────────────────────── състояние, което се запомня ───────────────────── */

/** Плътност на редовете — влияе само на височината и padding-а. */
export type Density = "compact" | "normal" | "roomy";

export interface SortRule {
  id: string;
  desc: boolean;
}

export interface ColumnFilterRule {
  id: string;
  value: unknown;
}

/** Вдига се при несъвместима промяна — запомненото състояние се изхвърля. */
export const DATA_TABLE_STATE_VERSION = 1;

export interface DataTableStateSnapshot {
  version: number;
  sorting: SortRule[];
  columnOrder: string[];
  columnVisibility: Record<string, boolean>;
  columnSizing: Record<string, number>;
  density: Density;
  pageSize: number;
}

/* ───────────────────────────── сървърен режим ───────────────────────────── */

/**
 * Мостът към BE, когато сортирането, филтрирането и страницирането стават там.
 * `totals` ИДВА ОТ BE — в сървърен режим редът „Общо" не може да се сметне от
 * страницата, а BE-то го връща за целия период по активните филтри.
 */
export interface ServerTableBridge {
  rowCount: number;
  pageIndex: number;
  onPageIndexChange: (pageIndex: number) => void;
  /** Броят редове на страница се запомня в таблицата, но заявката е на
   *  страницата — затова тя научава за смяната оттук. Началната стойност се
   *  чете със `readPersistedPageSize`, за да няма излишна първа заявка. */
  onPageSizeChange?: (pageSize: number) => void;
  onSortingChange: (rules: SortRule[]) => void;
  onColumnFiltersChange: (rules: ColumnFilterRule[]) => void;
  totals?: Record<string, number>;
}

/* ─────────────────────────── props на компонента ────────────────────────── */

export interface DataTableProps<TData> {
  /** Ключът, под който се пази подредбата на потребителя в localStorage. */
  tableId: string;
  data: readonly TData[];
  /** Резултатът от `createColumnHelper<TData>()`; типът е нарочно широк. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: any[];
  getRowId?: (row: TData, index: number) => string;

  mode?: "client" | "server";
  /** Задължителен при `mode: "server"`. */
  server?: ServerTableBridge;

  /** Начално сортиране, когато няма запомнено. */
  initialSorting?: SortRule[];

  loading?: boolean;
  /** Има данни, но тече презареждане — редовете се затъмняват, не изчезват. */
  refreshing?: boolean;

  /** Заглавен блок на картата (kicker + брой + хинт). */
  title?: ReactNode;
  /** Допълнителни контроли вдясно в тулбара (напр. Excel). */
  toolbarRight?: ReactNode;
  /** Филтри непосредствено след търсачката (напр. чип „Сервитьори"). */
  toolbarFilters?: ReactNode;
  /** Глобална търсачка. В сървърен режим текстът се вдига навън. */
  search?: {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
  };

  onRowClick?: (row: TData) => void;
  /** Тон на реда — подсветва отворената сметка, проблемния ред и т.н. */
  rowTone?: (row: TData) => "brand" | "danger" | null;

  emptyTitle?: string;
  emptyHint?: string;

  /** Мобилна карта вместо хоризонтален скрол. Липсва ли — общата транспозиция. */
  mobileCard?: (row: TData) => ReactNode;

  pageSize?: number;
  pageSizeOptions?: number[];

  /**
   * Редовете СЛЕД филтър и подредба, без страниране — само в клиентски режим.
   * Справката ги ползва за Excel: изнася се това, което човек вижда, не суровият
   * набор. Подавай СТАБИЛНА функция (`useCallback`), иначе ефектът се пуска на
   * всеки рендер.
   */
  onFilteredRowsChange?: (rows: TData[]) => void;

  /** Задължителен — таблицата е основното съдържание на екрана. */
  ariaLabel: string;
}
